import {
	ReactElement,
	useCallback
} from "react";
import {
	BulletList,
	FormattedText
} from "client/Components";
import {
	AlertManager,
	useFileSelector
} from "client/Hooks";
import {
	getDataUrl,
	isNotNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	CommunityPostImageFormModelKind,
	MAX_IMAGES_PER_POST,
	CommunityPostImageFormData,
	PostComposerFormData
} from "./common";
import { PostComposerOptions } from "./NewPostComposer";
import { formatList } from "client/Misc/cultureSpecificUtils";
import { Base64FileUriUploadWithMetadataModel, fileMetadataSymbol } from "client/Misc/commonTypes";
import { never, UnreachableCaseError } from "client_legacy/ndaw/errors";
import {
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import {
	PendingImageDownload,
	useSelectImageFromDocumentPicker
} from "../../useSelectImageFromDocumentPicker";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { PostComposerSelectImageSourceKind } from "./CommunityPostComposerFooter";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { IValueInputAttachable } from "client/Forms/attachable";
import { useCompressImageBeforeUpload } from "../../CompressImageDialog/useCompressImageBeforeUpload";

export type UseUploadCommunityPostImageResult = {
	onSelectImages: (source: PostComposerSelectImageSourceKind) => Promise<void>;
	dialogElement: ReactElement | null;
};

export const useUploadCommunityPostImage = (
	attachable: IValueInputAttachable<PostComposerFormData>,
	onIsBusyChange: (isLoading: boolean) => void,
	options: PostComposerOptions,
	alertManager: AlertManager
): UseUploadCommunityPostImageResult => {
	const imagesAttachable: IValueInputAttachable<readonly CommunityPostImageFormData[]> = attachable.for("images");

	const [compressImageDialog, resolveFileForUpload] = useCompressImageBeforeUpload(
		options.maxImageSize,
		onIsBusyChange
	);

	const removeLoadingImageHandler = useCallback(
		(loadingImageId: Guid): void => {
			imagesAttachable.reducer
				.remove(
					(image): boolean => image.kind === CommunityPostImageFormModelKind.Loading
						&& image.id === loadingImageId
				);
		},
		[imagesAttachable]
	);

	const selectFileImagesHandler = useCallback(
		async (...selectedFiles: readonly File[]): Promise<void> => {
			const currentImagesCount: number = imagesAttachable.value
				.length;

			const files: File[] = [];

			for (const selectedFile of selectedFiles) {
				const file: File = await resolveFileForUpload(selectedFile);

				files.push(file);
			}

			const selectionResult: ImageSelectionResult = await tryHandleImageSelection(
				files,
				currentImagesCount,
				alertManager,
				options
			);

			if (selectionResult.addedImages.length > 0) {
				imagesAttachable.reducer
					.push(...selectionResult.addedImages);
			}

			imagesAttachable.validation
				.triggerValueChanged();
		},
		[
			alertManager,
			imagesAttachable,
			options,
			resolveFileForUpload
		]
	);

	const {
		open: openFileExplorer
	} = useFileSelector({
		isMultiple: true,
		contentTypes: options.supportedImageTypes,
		onChange: selectFileImagesHandler
	});

	const {
		dialogElement: selectImageDialog,
		onSelectImage: selectImageFromWorkspaceHandler
	} = useSelectImageFromDocumentPicker({
		onIsBusyChange,
		supportedImageExtensions: options.supportedImageExtensions,
		defaultImageLocation: options.defaultImageLocation,
		isMultiple: true
	});

	const processWorkspaceImageSelections = useCallback(
		async (pendingImages: readonly PendingImageDownload[]): Promise<void> => {
			const loadingPlaceholders: ObjectWithClientId<CommunityPostImageFormData>[] = [];

			const pendingItems: PendingItem[] = [];

			for (const pendingImage of pendingImages) {
				const id: Guid = newGuid();

				loadingPlaceholders.push(
					setClientId({
						kind: CommunityPostImageFormModelKind.Loading,
						id,
						cancel: pendingImage.cancel,
					}),
				);

				pendingItems.push({
					id,
					file: pendingImage.file,
				});
			}

			imagesAttachable.reducer
				.push(...loadingPlaceholders);

			let processedImagesCount: number = 0;
			const currentImagesCount: number = imagesAttachable.value
				.length;

			const failedImageIds: Set<Guid> = new Set();

			const processImage = async (item: PendingItem): Promise<void> => {
				const {
					id,
					file: pendingFile
				} = item;

				try {
					const downloadedFile: File = await pendingFile;
					const file: File = await resolveFileForUpload(downloadedFile);

					const selectionResult: ImageSelectionResult = await tryHandleImageSelection(
						[file],
						currentImagesCount + processedImagesCount,
						alertManager,
						options,
					);

					if (
						selectionResult.addedImages
							.length === 0
					) {
						removeLoadingImageHandler(id);
						return;
					}

					const loadingImageIndex: number = imagesAttachable.value
						.findIndex(
							(x): boolean => x.kind === CommunityPostImageFormModelKind.Loading
								&& x.id === id
						);

					if (loadingImageIndex === -1) {
						throw new Error(`Could not find image with id ${id}`);
					}

					imagesAttachable.forIndex(loadingImageIndex)
						.reducer
						.setValue(selectionResult.addedImages[0]);

					processedImagesCount++;
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					failedImageIds.add(id);
					removeLoadingImageHandler(id);
				}
			};

			for (const pendingItem of pendingItems) {
				await processImage(pendingItem);
			}

			if (failedImageIds.size > 0) {
				const errorMessageKey: LocalizationKey = pendingImages.length > 1
					? "_ErrorWhileUploadingImagesPleaseTryAgain"
					: "_ErrorWhileUploadingTheImagePleaseTryAgain";

				alertManager.showError(localize(errorMessageKey));
			}
		},
		[
			alertManager,
			imagesAttachable,
			options,
			removeLoadingImageHandler,
			resolveFileForUpload
		],
	);

	const selectImagesHandler = useCallback(
		async (source: PostComposerSelectImageSourceKind): Promise<void> => {
			switch (source) {
				case "device":
					await openFileExplorer();
					break;
				case "workspace":
					const workspaceSelectionResult: TryResult<readonly PendingImageDownload[]> = await selectImageFromWorkspaceHandler();

					if (!workspaceSelectionResult.wasSuccessful) {
						return;
					}

					await processWorkspaceImageSelections(workspaceSelectionResult.result);

					break;
				default:
					never(source);
			}
		},
		[
			openFileExplorer,
			selectImageFromWorkspaceHandler,
			processWorkspaceImageSelections
		]
	);

	const dialogElement: ReactElement | nil = [
		selectImageDialog,
		compressImageDialog
	].find(isNotNil);

	return {
		onSelectImages: selectImagesHandler,
		dialogElement: dialogElement ?? null
	};
};

type PendingItem = {
	id: Guid;
	file: Promise<File>;
};

type ImageSelectionResult = {
	addedImages: readonly ObjectWithClientId<CommunityPostImageFormData>[];
};

async function tryHandleImageSelection(
	selectedFiles: readonly File[],
	currentImagesCount: number,
	alertManager: AlertManager,
	options: PostComposerOptions
): Promise<ImageSelectionResult> {
	const validFiles: File[] = [];

	let hasUnsupportedType: boolean = false;
	let hasFileTooLarge: boolean = false;

	for (const file of selectedFiles) {
		if (!options.supportedImageTypes.includes(file.type)) {
			hasUnsupportedType = true;
			continue;
		}

		if (file.size > options.maxImageSize) {
			hasFileTooLarge = true;
			continue;
		}

		validFiles.push(file);
	}

	const remainingCapacity: number = Math.max(MAX_IMAGES_PER_POST - currentImagesCount, 0);

	const filesToAdd: readonly File[] = validFiles.slice(0, remainingCapacity);

	const addedImages: ObjectWithClientId<CommunityPostImageFormData>[] = await Promise.all(
		filesToAdd.map(convertFileToFormData)
	);

	const hasTooManyImages: boolean = validFiles.length > filesToAdd.length;

	if (hasUnsupportedType
		|| hasFileTooLarge
		|| hasTooManyImages
	) {
		alertManager.showError(
			getImageSelectionAlertContent(
				{
					selectedFiles,
					hasUnsupportedType,
					hasFileTooLarge,
					hasTooManyImages,
					areSomeFilesAdded: addedImages.length > 0
				},
				options.maxImageSize
			)
		);
	}

	return {
		addedImages
	};
}

async function convertFileToFormData(
	file: File
): Promise<ObjectWithClientId<CommunityPostImageFormData>> {
	const dataUrl: string = await getDataUrl(file);

	const image: Base64FileUriUploadWithMetadataModel = {
		filename: file.name,
		uri: dataUrl,
		[fileMetadataSymbol]: {
			size: file.size,
			mimeType: file.type
		}
	};

	return setClientId({
		kind: CommunityPostImageFormModelKind.New,
		image: image
	});
}

type GetImageSelectionAlertContentInput = {
	selectedFiles: readonly File[];
	hasUnsupportedType: boolean;
	hasFileTooLarge: boolean;
	hasTooManyImages: boolean;
	areSomeFilesAdded: boolean;
};

function getImageSelectionAlertContent(
	input: GetImageSelectionAlertContentInput,
	maxImageSize: number
): ReactElement {
	const {
		selectedFiles,
		hasUnsupportedType,
		hasFileTooLarge,
		hasTooManyImages,
		areSomeFilesAdded
	} = input;

	if (selectedFiles.length === 1) {
		const selectedFileName: string = selectedFiles[0].name;

		if (hasUnsupportedType) {
			return (
				<FormattedText
					text={localize("_ImageCouldNotBeAddedUnsupportedFileType")}
					thingsToReplace={[
						{
							what: "{{FileName}}",
							filler: selectedFileName
						},
						{
							what: "{{SupportedFormats}}",
							filler: formatList([
								localize("_FileExtensionJpg"),
								localize("_FileExtensionPng"),
								localize("_FileExtensionGif")
							])
						}
					]}
				/>
			);
		}

		if (hasFileTooLarge) {
			return (
				<FormattedText
					text={localize("_ImageCouldNotBeAddedMaximumFileSize")}
					thingsToReplace={[
						{
							what: "{{FileName}}",
							filler: selectedFileName
						},
						{
							what: "{{MaxSize}}",
							filler: getDisplayFriendlySize(maxImageSize)
						}
					]}
				/>
			);
		}

		if (hasTooManyImages) {
			return (
				<FormattedText
					text={localize("_ImageCouldNotBeAddedMaximumCountImagesPerPost")}
					thingsToReplace={[
						{
							what: "{{FileName}}",
							filler: selectedFileName
						},
						{
							what: "{{Count}}",
							filler: `${MAX_IMAGES_PER_POST}`
						}
					]}
				/>
			);
		}

		throw new UnreachableCaseError("1 of the error booleans should have handled it" as never);
	} else {
		return (
			<>
				{localize(
					areSomeFilesAdded
						? "_SomeImagesWereNotAdded"
						: "_ImagesWereNotAdded"
				)}

				<BulletList trackingId="image-selection-errors">
					{hasTooManyImages && (
						<BulletList.Item>
							<FormattedText
								text={localize("_MaximumCountImagesPerPost")}
								thingsToReplace={[
									{
										what: "{{Count}}",
										filler: `${MAX_IMAGES_PER_POST}`
									}
								]}
							/>
						</BulletList.Item>
					)}

					{hasUnsupportedType && (
						<BulletList.Item>
							<FormattedText
								text={localize("_UnsupportedFileTypeSupportedFormats")}
								thingsToReplace={[
									{
										what: "{{SupportedFormats}}",
										filler: formatList([
											localize("_FileExtensionJpg"),
											localize("_FileExtensionPng"),
											localize("_FileExtensionGif")
										])
									}
								]}
							/>
						</BulletList.Item>
					)}

					{hasFileTooLarge && (
						<BulletList.Item>
							<FormattedText
								text={localize("_FileSizeExceedsMaxSize")}
								thingsToReplace={[
									{
										what: "{{MaxSize}}",
										filler: getDisplayFriendlySize(maxImageSize)
									}
								]}
							/>
						</BulletList.Item>
					)}
				</BulletList>
			</>
		);
	}
}
